// Admin-facing REST service for hallpass-backend's schools-api per-student
// pass limit overrides. See src/api/config.js's SCHOOLS_API_BASE and
// src/api/http.js's schoolsApi().
//
// studentPassLimitRouter is mounted at the bare :schoolId level, not a
// fixed sub-path (apps/schools-api/src/app.ts: `schoolRouter.use("/:schoolId",
// studentPassLimitRouter)`), because the router itself defines two distinct
// URL shapes (apps/schools-api/src/routes/student-pass-limit.ts):
//   GET    /api/schools/:schoolId/pass-limits                    ADMIN/SUPER_ADMIN — lists every override for the school
//   GET    /api/schools/:schoolId/students/:studentId/pass-limit ADMIN/SUPER_ADMIN
//   PUT    /api/schools/:schoolId/students/:studentId/pass-limit ADMIN/SUPER_ADMIN — partial-update upsert
//   DELETE /api/schools/:schoolId/students/:studentId/pass-limit ADMIN/SUPER_ADMIN
//
// Unlike schoolConfigApi.js's putPolicy (a full-replace upsert), PUT here is
// a partial update on an existing row: only the keys the caller sends are
// written (`update: req.body`), so setting one field never resets the
// other back to its default.

import { schoolsApi } from './http';
import type { components } from './types.gen';

type StudentPassLimitListResponse =
  components['schemas']['StudentPassLimitListResponse'];
type StudentPassLimitResponse =
  components['schemas']['StudentPassLimitResponse'];
type UpsertStudentPassLimitBody =
  components['schemas']['UpsertStudentPassLimitBody'];

/**
 * List every per-student pass limit override configured for the school.
 *
 * @param schoolId
 * @returns each row: {id, schoolId, studentId, maxPerInterval, unlimited}
 */
export function listOverrides(
  schoolId: number | string
): Promise<StudentPassLimitListResponse> {
  return schoolsApi<StudentPassLimitListResponse>(
    `/api/schools/${schoolId}/pass-limits`
  );
}

/**
 * Fetch a single student's pass limit override.
 *
 * @param schoolId
 * @param studentId
 * @returns {id, schoolId, studentId, maxPerInterval, unlimited}
 * @throws {import('./http').ApiError} 404 "Student not found"; 404 "No pass limit override set for this student"
 */
export function getStudentLimit(
  schoolId: number | string,
  studentId: number | string
): Promise<StudentPassLimitResponse> {
  return schoolsApi<StudentPassLimitResponse>(
    `/api/schools/${schoolId}/students/${studentId}/pass-limit`
  );
}

/**
 * Upsert a student's pass limit override. Only the keys present in `body`
 * are written on update (create defaults `maxPerInterval` to null and
 * `unlimited` to false for any key omitted). Setting a non-null
 * `maxPerInterval` requires the school to already have a PassPolicy
 * `interval` configured (schoolConfigApi.js's putPolicy).
 *
 * @param schoolId
 * @param studentId
 * @param body
 * @returns the upserted override
 * @throws {import('./http').ApiError} 404 "Student not found"; 422 if maxPerInterval is set without a school policy interval configured
 */
export function updateStudentLimit(
  schoolId: number | string,
  studentId: number | string,
  body: UpsertStudentPassLimitBody
): Promise<StudentPassLimitResponse> {
  return schoolsApi<StudentPassLimitResponse>(
    `/api/schools/${schoolId}/students/${studentId}/pass-limit`,
    { method: 'PUT', body }
  );
}

/**
 * @param schoolId
 * @param studentId
 * @returns null
 * @throws {import('./http').ApiError} 404 "No pass limit override set for this student"
 */
export function deleteStudentLimit(
  schoolId: number | string,
  studentId: number | string
): Promise<null> {
  return schoolsApi<null>(
    `/api/schools/${schoolId}/students/${studentId}/pass-limit`,
    { method: 'DELETE' }
  );
}
